import { Component, Input } from '@angular/core';
import { AssessmentThumbnailConfig } from '../../core-data-model/AssessmentThumbnailConfig';
import { BehavioralAssessment } from '../../core-data-model/BehavioralAssessment.model';
import { FullPattern } from '../../core-data-model/Pattern.model';
import { AssessmentsApi } from '../../core-services/AssessmentsApi.connect';
import { DateFormatUtility } from '../../core-utilities/DateFormat.utility';
import { TranslationService } from '../../translations';

enum AssessmentThumbnailModalType {
    Behavioral,
    Cognitive
}

@Component({
    selector: 'assessment-thumbnail',
    // TODO: move this ngIf/logic out of the template
    template: `
        <div
            tooltip
            [tooltipContent]="properties.tooltip"
            [tooltipDisabled]="!properties.tooltip"
            [tooltipAnimation]="true"
            [tooltipDelay]="500"
            tooltipPlacement="top"
            (click)="openInModal($event)"
            style="cursor: pointer"
        >
            @if (properties.svgType === 'cognitive') {
                <div>
                    <cognitive-thumbnail
                        [personCognitiveScore]="properties.personCognitiveScore"
                        [jobCognitiveScore]="properties.jobCognitiveScore"
                    ></cognitive-thumbnail>
                </div>
            }
            @if (properties.svgType === 'behavioral') {
                <div>
                    <behavioral-thumbnail
                        [type]="properties.behavioralType"
                        [pattern]="properties.pattern"
                    ></behavioral-thumbnail>
                </div>
            }
            @if (properties.svgType === 'jobTarget') {
                <div>
                    <behavioral-ranges
                        [ranges]="properties.jobPatternRanges"
                        [hoveredPattern]="properties.pattern"
                        [showAsThumbnail]="true"
                    >
                    </behavioral-ranges>
                </div>
            }
        </div>

        @if (showModal) {
            <modal class="pi-modal">
                <div class="pi-modalHeader">
                    <span class="flex-1">
                        {{ modalTitle }}
                    </span>
                    <button
                        type="button"
                        class="close"
                        (click)="showModal = false"
                        [title]="TR.key('text.common-terms.close')"
                    >
                        <i
                            class="fa fa-close"
                            [title]="TR.key('text.common-terms.close')"
                        ></i>
                    </button>
                </div>
                <div class="pi-modalBody">
                    @if (
                        modalType === AssessmentThumbnailModalType.Behavioral
                    ) {
                        <behavioral-pattern
                            [behavioralPattern]="behavioralPattern"
                            [viewAll]="true"
                        >
                        </behavioral-pattern>
                    }
                    @if (modalType === AssessmentThumbnailModalType.Cognitive) {
                        <cognitive-assessment-image [personScore]="personScore">
                        </cognitive-assessment-image>
                    }
                </div>
            </modal>
        }
    `,
    standalone: false
})
export class AssessmentThumbnailComponent {
    AssessmentThumbnailModalType = AssessmentThumbnailModalType;

    @Input()
    properties: AssessmentThumbnailConfig;

    // The data retrieved that powers this thumbnail may not include the original
    // assessment id. If this is true you can set this to true and instead the
    // launched modal will figure out right pattern from the person id.
    @Input()
    originalIdIncorrect = false;

    showModal = false;
    modalType: AssessmentThumbnailModalType | null = null;

    // State for modals (is there a better way to do this)
    modalTitle = '';
    behavioralPattern: FullPattern = null;
    personScore: number = null;

    constructor(
        private assessmentsApi: AssessmentsApi,
        private dateFormatUtil: DateFormatUtility,
        public TR: TranslationService
    ) {}

    async openInModal(event: MouseEvent) {
        event.stopPropagation();
        this.showModal = true;

        if (this.properties.personOrJob.isPerson) {
            if (this.properties.svgType === 'behavioral') {
                const assessment =
                    await this.assessmentsApi.getBehavioralAssessment(
                        await this.determineOriginalId(this.properties)
                    );
                const titleTime = assessment.completedDate
                    ? `(${this.dateFormatUtil.transformDate(
                          assessment.completedDate
                      )})`
                    : '';

                this.behavioralPattern = assessment.pattern;
                this.modalTitle = `${this.properties.lastName}, ${this.properties.firstName} ${titleTime}`;
                this.modalType = AssessmentThumbnailModalType.Behavioral;
            } else {
                // TODO using sent date here - may make more sense to use completed date
                const titleTime = this.properties.cognitiveCompletedDate
                    ? `(${this.dateFormatUtil.transformDate(
                          this.properties.cognitiveCompletedDate
                      )})`
                    : '';

                this.modalType = AssessmentThumbnailModalType.Cognitive;
                this.modalTitle = `${this.properties.lastName}, ${this.properties.firstName} ${titleTime}`;
                this.personScore = this.properties.personCognitiveScore;
            }
        } else {
            // TODO:add a job modal here to display the full job target ranges as a modal
        }
    }

    private async determineOriginalId(props: AssessmentThumbnailConfig) {
        if (this.originalIdIncorrect) {
            const assessmentList =
                await this.assessmentsApi.getAllBehavioralAssessmentsForPerson(
                    this.properties.personOrJob.id
                );
            const originalAssessment = assessmentList
                .filter((a) => a.hasPattern())
                .reduce(
                    (
                        lastValue: BehavioralAssessment,
                        currentValue: BehavioralAssessment
                    ) =>
                        lastValue.completedDate.getTime() >
                        currentValue.sentDate.getTime()
                            ? currentValue
                            : lastValue
                );
            return originalAssessment.id;
        } else {
            return props.assessmentId;
        }
    }
}
